/**
 * Email Template Builder
 * Provides a unified HTML template for all email notifications
 */

interface EmailTemplateParams {
  recipientName: string;
  subject: string;
  message: string;
  footer?: string;
}

export const buildEmailTemplate = ({
  recipientName,
  subject,
  message,
  footer = 'This is an automated message from GHAFES Library. Please do not reply to this email.',
}: EmailTemplateParams): string => {
  return `
<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${subject}</title>
  <style>
    body {
      margin: 0;
      padding: 0;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
      background-color: #f5f5f5;
    }
    .email-container {
      max-width: 600px;
      margin: 0 auto;
      background-color: #ffffff;
    }
    .header {
      background: linear-gradient(135deg, #1e3a5f 0%, #2d5a8c 100%);
      padding: 30px 20px;
      text-align: center;
    }
    .logo {
      color: #ffffff;
      font-size: 24px;
      font-weight: bold;
      margin: 0;
    }
    .content {
      padding: 40px 30px;
      color: #333333;
      line-height: 1.6;
    }
    .greeting {
      font-size: 18px;
      font-weight: 600;
      color: #1e3a5f;
      margin-bottom: 20px;
    }
    .message {
      margin-bottom: 30px;
    }
    .footer {
      background-color: #f8f9fa;
      padding: 20px 30px;
      text-align: center;
      color: #6c757d;
      font-size: 14px;
      border-top: 1px solid #e9ecef;
    }
    .divider {
      height: 1px;
      background-color: #e9ecef;
      margin: 30px 0;
    }
    .info-box {
      background-color: #f8f9fa;
      border-left: 4px solid #1e3a5f;
      padding: 15px;
      margin: 20px 0;
    }
    .warning-box {
      background-color: #fff3cd;
      border-left: 4px solid #ffc107;
      padding: 15px;
      margin: 20px 0;
      color: #856404;
    }
    .success-box {
      background-color: #d4edda;
      border-left: 4px solid #28a745;
      padding: 15px;
      margin: 20px 0;
      color: #155724;
    }
    strong {
      color: #1e3a5f;
    }
    a {
      color: #1e3a5f;
      text-decoration: none;
    }
    a:hover {
      text-decoration: underline;
    }
  </style>
</head>
<body>
  <div class="email-container">
    <div class="header">
      <h1 class="logo">📚 GHAFES Library</h1>
    </div>
    <div class="content">
      <div class="greeting">Hello ${recipientName},</div>
      <div class="message">
        ${message}
      </div>
    </div>
    <div class="footer">
      ${footer}
      <br><br>
      &copy; ${new Date().getFullYear()} GHAFES Library. All rights reserved.
    </div>
  </div>
</body>
</html>
  `.trim();
};

/**
 * Build HTML message for new admin notification
 */
export const buildNewAdminMessage = (data: {
  newAdminName: string;
  newAdminEmail: string;
  newAdminTitle: string;
  addedByName: string;
  addedByEmail: string;
  date: string;
}): string => {
  return `
    <p>A new administrator has been added to the GHAFES Library system.</p>

    <div class="info-box">
      <strong>New Administrator Details:</strong><br>
      <strong>Name:</strong> ${data.newAdminName}<br>
      <strong>Email:</strong> ${data.newAdminEmail}<br>
      <strong>Title:</strong> ${data.newAdminTitle}<br>
      <strong>Added on:</strong> ${data.date}
    </div>

    <div class="info-box">
      <strong>Added by:</strong><br>
      <strong>Name:</strong> ${data.addedByName}<br>
      <strong>Email:</strong> ${data.addedByEmail}
    </div>

    <p>This is a notification to keep all administrators informed of system changes.</p>
  `;
};

/**
 * Build HTML message for welcome admin email
 */
export const buildWelcomeAdminMessage = (data: {
  adminTitle: string;
  tempPassword: string;
  addedByName: string;
  loginUrl: string;
  date: string;
}): string => {
  return `
    <p>Welcome to the GHAFES Library administration team!</p>

    <p>You have been added as <strong>${data.adminTitle}</strong> by <strong>${data.addedByName}</strong> on ${data.date}.</p>

    <div class="success-box">
      <strong>Your Login Credentials:</strong><br><br>
      <strong>Temporary Password:</strong> <code style="background: #fff; padding: 5px 10px; border: 1px solid #ddd; border-radius: 4px; font-family: monospace;">${data.tempPassword}</code>
    </div>

    <div class="warning-box">
      <strong>⚠️ Important Security Notice:</strong><br><br>
      For security reasons, it is <strong>highly advisable to change your password</strong> immediately after your first login.
      You can change your password from your profile settings.
    </div>

    <p>To access the library system:</p>
    <ol>
      <li>Visit the GHAFES Library: <a href="${data.loginUrl}" target="_blank">${data.loginUrl}</a></li>
      <li>Click on "Login" and use your email with the temporary password provided above</li>
      <li>After logging in, navigate to your profile and change your password</li>
    </ol>

    <p>If you have any questions or need assistance, please contact the administrator who added you.</p>

    <p>We're excited to have you on the team!</p>
  `;
};

/**
 * Build HTML message for new book notification
 */
export const buildNewBookMessage = (data: {
  bookTitle: string;
  bookAuthor: string;
  bookIsbn: string;
  bookCategory: string;
  addedByName: string;
  date: string;
}): string => {
  return `
    <p>A new book has been added to the GHAFES Library collection.</p>

    <div class="info-box">
      <strong>Book Details:</strong><br>
      <strong>Title:</strong> ${data.bookTitle}<br>
      <strong>Author:</strong> ${data.bookAuthor}<br>
      <strong>ISBN:</strong> ${data.bookIsbn}<br>
      <strong>Category:</strong> ${data.bookCategory}<br>
      <strong>Added on:</strong> ${data.date}
    </div>

    <div class="info-box">
      <strong>Added by:</strong> ${data.addedByName}
    </div>

    <p>The book is now available for borrowing by library members.</p>
  `;
};
